import {
  Box,
  Checkbox,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Position, useEdges } from 'reactflow';
import { useAuthStore } from '../../../store/storeAuth';
import { isActiveEdge } from '../../../util/findActiveHandle';
import { ThirdPartyIntegrationService } from '../../../service/ThirdPartyIntegrationService';
import InputHandle from '../../UI/InputHandle/InputHandle';
import Label from '../../UI/Label/Label';
import TagInput from '../../Test/TagInput';
import type { FieldDefinition } from '../types';
import { nodeColorBasedOnType } from '../../UI/Node/nodeConstants';

const EMPTY_DATA_CONNECTED: { id?: string; text?: string; label?: string }[] = [];

interface DynamicFieldRendererProps {
  fields: FieldDefinition[];
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
  onFieldFocus?: (name: string) => void;
  onFieldBlur?: () => void;
  nodeId: string;
  nodeType?: string;
  provider: string;
  context?: Record<string, unknown>;
  disabled?: boolean;
  connected?: boolean;
  nodeData?: Record<string, unknown>;
  errorFieldName?: string | null;
}

function normalizeOptions(data: unknown): { label: string; value: string }[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && 'label' in data && 'value' in data) {
    return [data as { label: string; value: string }];
  }
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    if (Array.isArray(o.options)) return o.options as { label: string; value: string }[];
    if (Array.isArray(o.items)) return o.items as { label: string; value: string }[];
  }
  return [];
}

function meetsDependsOn(
  dep: FieldDefinition['dependsOn'],
  values: Record<string, unknown>,
): boolean {
  if (!dep) return true;
  const current = values[dep.field];
  if (Array.isArray(dep.value)) {
    return dep.value.some(
      v => v === current || (v === 'true' && current === true) || (v === 'false' && current === false),
    );
  }
  return current === dep.value || (dep.value === 'true' && current === true) || (dep.value === 'false' && current === false);
}

const errorBorderSx = (themeDark: boolean) => ({
  '& .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${themeDark ? '#F59E0B' : '#EAB308'}`,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${themeDark ? '#F59E0B' : '#EAB308'}`,
  },
});

const DynamicFieldRenderer: React.FC<DynamicFieldRendererProps> = ({
  fields,
  values,
  onChange,
  onFieldFocus,
  onFieldBlur,
  nodeId,
  nodeType = 'thirdPartyIntegration',
  provider,
  context,
  disabled,
  connected,
  nodeData,
  errorFieldName,
}) => {
  const { user } = useAuthStore(state => state);
  const edges = useEdges();
  const [dynamicOptions, setDynamicOptions] = useState<
    Record<string, { label: string; value: string }[]>
  >({});
  const [loadingFields, setLoadingFields] = useState<Record<string, boolean>>({});

  const nodeColors =
    nodeColorBasedOnType[nodeType as keyof typeof nodeColorBasedOnType]
      ?.colors ?? nodeColorBasedOnType.pullData?.colors;
  const findColor = nodeColors ?? { color: '#3650DE' };

  const selectSx = {
    width: '100%',
    maxWidth: '360px',
    '& .MuiList-root': {
      backgroundColor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
    },
    borderRadius: '8px',
    '.MuiOutlinedInput-notchedOutline': { border: 0 },
    bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
  };

  const integrationId = nodeData?.integrationId as string | undefined;
  const connectedByUserId = nodeData?.connectedByUserId as string | undefined;
  const contextKey = context ? JSON.stringify(context) : '';
  const fieldNamesKey = fields.map(f => f.name).join(',');

  useEffect(() => {
    const dynamicFields = fields.filter(f => f.options === 'dynamic');
    if (dynamicFields.length === 0 || !connected) return;
    let cancelled = false;
    const initialLoading: Record<string, boolean> = {};
    dynamicFields.forEach(f => { initialLoading[f.name] = true; });
    setLoadingFields(initialLoading);
    const optionsContext = { ...context };
    if (integrationId) {
      (optionsContext as Record<string, unknown>).integrationId = integrationId;
    } else if (connectedByUserId) {
      (optionsContext as Record<string, unknown>).connectedByUserId = connectedByUserId;
    }
    const load = async () => {
      for (const f of dynamicFields) {
        try {
          const res = await ThirdPartyIntegrationService.getDynamicOptions(
            provider,
            f.name,
            optionsContext,
          );
          if (!cancelled) {
            setDynamicOptions(prev => ({
              ...prev,
              [f.name]: res?.data !== undefined ? normalizeOptions(res.data) : [],
            }));
            setLoadingFields(prev => ({ ...prev, [f.name]: false }));
          }
        } catch {
          if (!cancelled) {
            setDynamicOptions(prev => ({ ...prev, [f.name]: [] }));
            setLoadingFields(prev => ({ ...prev, [f.name]: false }));
          }
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [provider, contextKey, fieldNamesKey, connected, integrationId, connectedByUserId]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {fields.map(field => {
        if (!meetsDependsOn(field.dependsOn, values)) return null;

        const value = values[field.name] ?? field.defaultValue ?? '';
        const hasHandle = (field.readOnly ? false : field.hasConnectionPoint) ?? false;
        const handleId = `field_${field.name}`;
        const isActive = isActiveEdge(edges, nodeId, handleId, 'target');
        const hasError = errorFieldName != null && field.name === errorFieldName;

        const inputSx = {
          width: '100%',
          maxWidth: '360px',
          '& .MuiOutlinedInput-root': {
            bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
            '& fieldset': { borderColor: user?.theme === 'dark' ? '#475467' : '#D0D5DD' },
          },
        };

        const isSelectType =
          field.type === 'select' || field.type === 'multiselect';

        const renderField = () => {
          switch (field.type) {
            case 'text':
              return (
                <TextField
                  className="nodrag"
                  size="small"
                  fullWidth
                  placeholder={field.placeholder}
                  value={(value as string) ?? ''}
                  onChange={e => onChange(field.name, e.target.value)}
                  onFocus={() => onFieldFocus?.(field.name)}
                  onBlur={onFieldBlur}
                  disabled={disabled}
                  error={hasError}
                  helperText={hasError ? 'Required' : undefined}
                  inputProps={{
                    maxLength: field.validation?.maxLength,
                    ...(field.readOnly ? { readOnly: true } : {}),
                  }}
                  sx={{
                    ...inputSx,
                    ...(hasError && errorBorderSx(user?.theme === 'dark')),
                    ...(field.readOnly
                      ? {
                          '& .MuiInputBase-input': { cursor: 'text', caretColor: 'transparent' },
                        }
                      : {}),
                  }}
                />
              );
            case 'textarea':
              return (
                <TextField
                  className="nodrag"
                  size="small"
                  multiline
                  minRows={2}
                  placeholder={field.placeholder}
                  value={(value as string) ?? ''}
                  onChange={e => onChange(field.name, e.target.value)}
                  onFocus={() => onFieldFocus?.(field.name)}
                  onBlur={onFieldBlur}
                  disabled={disabled}
                  fullWidth
                  error={hasError}
                  helperText={hasError ? 'Required' : undefined}
                  sx={{ ...inputSx, ...(hasError && errorBorderSx(user?.theme === 'dark')) }}
                />
              );
            case 'number':
              return (
                <TextField
                  className="nodrag"
                  size="small"
                  type="number"
                  placeholder={field.placeholder}
                  value={(value as number) ?? ''}
                  onChange={e =>
                    onChange(
                      field.name,
                      e.target.value === ''
                        ? ''
                        : Number(e.target.value),
                    )
                  }
                  onFocus={() => onFieldFocus?.(field.name)}
                  onBlur={onFieldBlur}
                  disabled={disabled}
                  fullWidth
                  error={hasError}
                  helperText={hasError ? 'Required' : undefined}
                  sx={{ ...inputSx, ...(hasError && errorBorderSx(user?.theme === 'dark')) }}
                />
              );
            case 'select': {
              const isDynamic = field.options === 'dynamic';
              const isLoading = isDynamic && loadingFields[field.name] === true;
              const rawOptions = isDynamic ? dynamicOptions[field.name] : field.options;
              const options = Array.isArray(rawOptions) ? rawOptions : [];
              const selectedLabel = options.find(o => o.value === (value as string))?.label;
              const isRequired = field.validation?.required === true;
              const isEmpty = value === undefined || value === null || value === '';
              const showErrorState = hasError || (isRequired && isEmpty);
              const isDisabled = disabled || isLoading || (isDynamic && !isLoading && options.length === 0);
              const placeholder =
                isLoading
                  ? 'Loading...'
                  : field.placeholder ?? `Select ${field.label}`;
              return (
                <Select
                  className="nodrag"
                  size="small"
                  value={(value as string) ?? ''}
                  onChange={e => onChange(field.name, e.target.value)}
                  onFocus={() => onFieldFocus?.(field.name)}
                  onBlur={onFieldBlur}
                  disabled={isDisabled}
                  required={isRequired}
                  error={showErrorState}
                  sx={{
                    ...selectSx,
                    ...(showErrorState && errorBorderSx(user?.theme === 'dark')),
                  }}
                  displayEmpty
                  renderValue={() => selectedLabel ?? placeholder}
                >
                  {!isRequired && (
                    <MenuItem value="" sx={{ color: user?.theme === 'dark' ? '#D0D5DD' : 'black' }}>
                      —
                    </MenuItem>
                  )}
                  {options.map(opt => (
                    <MenuItem
                      key={opt.value}
                      value={opt.value}
                      sx={{ color: user?.theme === 'dark' ? '#D0D5DD' : 'black' }}
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              );
            }
            case 'multiselect': {
              const isDynamic = field.options === 'dynamic';
              const isLoading = isDynamic && loadingFields[field.name] === true;
              const rawOptions = isDynamic ? dynamicOptions[field.name] : field.options;
              const options = Array.isArray(rawOptions) ? rawOptions : [];
              const rawValue = value as string | string[] | undefined;
              const selectedValues: string[] = Array.isArray(rawValue)
                ? rawValue
                : rawValue
                  ? String(rawValue).split(',').filter(Boolean)
                  : [];
              const isDisabled = disabled || isLoading || (isDynamic && !isLoading && options.length === 0);
              const placeholder = field.placeholder ?? `Select ${field.label}`;
              const displayLabel = isLoading
                ? 'Loading...'
                : selectedValues.length > 0
                  ? options
                      .filter(o => selectedValues.includes(o.value))
                      .map(o => o.label)
                      .join(', ') || placeholder
                  : placeholder;
              return (
                <Select
                  className="nodrag"
                  size="small"
                  multiple
                  value={selectedValues}
                  onChange={e => {
                    const val = e.target.value as string[];
                    onChange(field.name, val);
                  }}
                  onFocus={() => onFieldFocus?.(field.name)}
                  onBlur={onFieldBlur}
                  disabled={isDisabled}
                  sx={selectSx}
                  displayEmpty
                  renderValue={() => displayLabel}
                >
                  {options.map(opt => (
                    <MenuItem
                      key={opt.value}
                      value={opt.value}
                      sx={{ color: user?.theme === 'dark' ? '#D0D5DD' : 'black', gap: 1 }}
                    >
                      <Checkbox
                        checked={selectedValues.includes(opt.value)}
                        size="small"
                        sx={{ p: 0 }}
                      />
                      <ListItemText primary={opt.label} />
                    </MenuItem>
                  ))}
                </Select>
              );
            }
            case 'datetime': {
              const strVal = (value as string) ?? '';
              const dateVal = strVal && dayjs(strVal).isValid() ? dayjs(strVal) : null;
              return (
                <DateTimePicker
                  className="nodrag"
                  label=""
                  value={dateVal}
                  onChange={(val: Dayjs | null) =>
                    onChange(field.name, val?.toISOString() ?? '')
                  }
                  disabled={disabled}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      error: hasError,
                      helperText: hasError ? 'Required' : undefined,
                      placeholder: field.placeholder as string,
                      onFocus: () => onFieldFocus?.(field.name),
                      onBlur: onFieldBlur,
                      sx: {
                        ...inputSx,
                        ...(hasError && errorBorderSx(user?.theme === 'dark')),
                        maxWidth: '360px',
                      },
                    },
                  }}
                />
              );
            }
            case 'toggle':
              return (
                <FormControlLabel
                  control={
                    <Switch
                      className="nodrag"
                      checked={Boolean(value)}
                      onChange={e => onChange(field.name, e.target.checked)}
                      onFocus={() => onFieldFocus?.(field.name)}
                      onBlur={onFieldBlur}
                      disabled={disabled}
                    />
                  }
                  label=""
                />
              );
            default:
              return (
                <TextField
                  className="nodrag"
                  size="small"
                  placeholder={field.placeholder}
                  value={(value as string) ?? ''}
                  onChange={e => onChange(field.name, e.target.value)}
                  onFocus={() => onFieldFocus?.(field.name)}
                  onBlur={onFieldBlur}
                  disabled={disabled}
                  fullWidth
                  error={hasError}
                  helperText={hasError ? 'Required' : undefined}
                  sx={{ ...inputSx, ...(hasError && errorBorderSx(user?.theme === 'dark')) }}
                />
              );
          }
        };

        const labelName = field.validation?.required ? `${field.label} *` : field.label;
        const rawDataConnected = nodeData?.[field.name + 'Data'];
        const dataConnected = Array.isArray(rawDataConnected)
          ? (rawDataConnected as { id?: string; text?: string; label?: string }[])
          : EMPTY_DATA_CONNECTED;

        if (hasHandle && nodeData && field.type !== 'datetime' && !isSelectType) {
          return (
            <Box key={field.name} sx={{ width: '100%' }}>
              <Box
                sx={
                  hasError
                    ? {
                        borderRadius: 1,
                        border: `1px solid ${user?.theme === 'dark' ? '#F59E0B' : '#EAB308'}`,
                        p: 0.5,
                      }
                    : undefined
                }
              >
                <TagInput
                  name={field.name}
                  value={(value as string) ?? ''}
                  onChange={(e: { target: { name: string; value: unknown } }) =>
                    onChange(field.name, e.target.value)
                  }
                  handleId={handleId}
                  nodeId={nodeId}
                  dataConnected={dataConnected}
                  isActive={isActive}
                  findColor={findColor}
                  labelName={labelName}
                  placeholder={field.placeholder as string}
                  disabled={disabled}
                  removeIcons
                  isCollapsed={true}
                  advancedCollapsed={true}
                />
              </Box>
              {hasError && (
                <Box
                  component="span"
                  sx={{
                    fontSize: '12px',
                    color: user?.theme === 'dark' ? '#F59E0B' : '#EAB308',
                    mt: 0.5,
                    display: 'block',
                  }}
                >
                  Required
                </Box>
              )}
            </Box>
          );
        }

        return (
          <Box
            key={field.name}
            sx={{ position: 'relative', width: '100%' }}
          >
            {hasHandle && (
              <InputHandle
                handleId={handleId}
                type="target"
                position={Position.Left}
                isActive={isActive}
                activeColor={findColor.color}
                left={-26}
                top="10px"
              />
            )}
            <Label
              labelName={labelName}
              isAdvanced
              labelStyle={{
                fontSize: '14px',
                color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                fontWeight: 400,
              }}
            />
            {renderField()}
          </Box>
        );
      })}
    </Box>
  );
};

export default DynamicFieldRenderer;
